import React from "react";
import styles from "./radio-details.module.scss";
import { useRouter } from "next/router";
import Loader from "app/components/icons/Loader";
import clsx from "clsx";
import { RadioView } from "app/components/radio-view/radio-view.component";
import { useNavigateBack } from "app/hooks/navigation.hook";
import Head from "next/head";
import { useGoBackHistory } from "app/hooks/go-back.hook";
import { useTranslate } from "app/hooks/lang";
import { BackButtonBar } from "app/components/back-button/back-button.component";
import { getPillowRadio } from "services-hooks/services";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import { getCircularReplacer } from "app/utils/array";
import { NextSeo } from "next-seo";
import { truncateString } from "app/utils/string";
import { Category } from "services-hooks/types";
import { getServersideLocale } from "app/utils/getServersideLocale";

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps<{
  initialData: Category;
}> = async (context) => {
  try {
    const permalink = context.params ? context.params["permalink"] : null;
    if (!permalink) {
      throw new Error("Missing permalink");
    }
    const radio: Category | undefined = (
      await getPillowRadio(`radios/${(permalink as string[])?.join("/")}`, {
        headers: {
          authorization: `Bearer ${process.env.SERVER_API_KEY}`,
          "X-PillowLocale": getServersideLocale(context.locale),
        },
      })
    ).data.body?.content;
    return {
      props: {
        initialData: JSON.parse(JSON.stringify(radio, getCircularReplacer())),
      },
      revalidate: 43200,
    };
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }
};

export default function RadioPage({
  initialData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter();
  return (
    <>
      {router.isFallback ? (
        <div className={styles.wrapper}>
          <div className={clsx("loader", styles.loader)}>
            <Loader />
          </div>
        </div>
      ) : (
        <RadioPageContent initialData={initialData} />
      )}
    </>
  );
}
interface RadioPageContentProps {
  initialData: Category;
}
const RadioPageContent: React.FC<RadioPageContentProps> = ({ initialData }) => {
  const router = useRouter();
  const { permalink } = router.query;
  const parsedPermalink = (permalink as string[])?.join("/") || "";

  const t = useTranslate();
  const goBack = useNavigateBack();
  useGoBackHistory("RADIOS/PERMALINK");
  const radioName = initialData.name;
  const radioDescription = initialData.description || "";
  const pageTags = initialData.tags
    ?.filter((tag) => tag.tagType !== "audioads")
    .map((tag) => tag.name)
    .join(", ");

  const openGraphImages = initialData.logo
    ? [
        {
          url: initialData.logo,
          alt: radioName,
        },
      ]
    : [];

  return (
    <>
      <Head>{pageTags && <meta name="keywords" content={pageTags} />}</Head>
      <NextSeo
        title={t("seo_title_radio", { var1: radioName })}
        titleTemplate={`%s`}
        description={`${t("seo_description_radio", {
          var1: radioName,
          var2: truncateString(radioDescription, 160),
        })}`.trim()}
        languageAlternates={[
          ...(router.locales || [])
            .filter((locale) => locale !== "defaultLang")
            .map((locale) => ({
              hrefLang: locale,
              href: `https://www.radioline.co/${locale}/radios/${parsedPermalink}`,
            })),
          {
            href: `https://www.radioline.co/en/radios/${parsedPermalink}`,
            hrefLang: "x-default",
          },
        ]}
        canonical={
          router.locale !== "defaultLang"
            ? `https://www.radioline.co/${router.locale}/radios/${parsedPermalink}`
            : `https://www.radioline.co/en/radios/${parsedPermalink}`
        }
        openGraph={{
          images: openGraphImages,
        }}
      />
      <BackButtonBar onClick={goBack} />
      <div className={styles.wrapper}>
        <RadioView radio={initialData} />
      </div>
    </>
  );
};
